import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { 
  Car, 
  Search, 
  Filter,
  Calendar,
  DollarSign,
  Package,
  Mail,
  Phone
} from 'lucide-react';

import { BACKEND_URL_EXPORT as BACKEND_URL, API } from '../../config';

const PublicMarketplace = () => {
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedType, setSelectedType] = useState('all');

  useEffect(() => {
    fetchPublicVehicles();
  }, []);

  const fetchPublicVehicles = async () => {
    try {
      const response = await axios.get(`${API}/public/vehicles`);
      setVehicles(response.data);
    } catch (error) {
      console.error('Error fetching vehicles:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredVehicles = vehicles.filter(vehicle => {
    const matchesSearch = 
      vehicle.model.toLowerCase().includes(searchTerm.toLowerCase()) ||
      vehicle.type.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesType = selectedType === 'all' || vehicle.type.toLowerCase().includes(selectedType.toLowerCase());
    
    return matchesSearch && matchesType;
  });

  const vehicleTypes = ['all', ...new Set(vehicles.map(v => v.type.toLowerCase()))];

  const handleInquiry = (vehicle) => {
    const subject = `Inquiry about ${vehicle.year} ${vehicle.model} (Stock: ${vehicle.stock_no})`;
    const body = `Hi, I'm interested in the ${vehicle.year} ${vehicle.model}. Stock Number: ${vehicle.stock_no}. Please provide more details.`;
    window.location.href = `mailto:sales@yourcompany.com?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center">
        <div className="text-center">
          <Car className="h-16 w-16 text-red-600 mx-auto mb-4 animate-bounce" />
          <p className="text-slate-600 text-lg">Loading vehicles...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-red-600 to-red-800 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-4">
              Premium Vehicles For Sale
            </h1>
            <p className="text-xl text-red-100 mb-8">
              Quality Japanese vehicles imported directly from auctions
            </p>
            <div className="flex items-center justify-center space-x-4">
              <Badge className="bg-white text-red-600 text-lg px-4 py-2">
                {vehicles.length} Vehicles Available
              </Badge>
            </div>
          </div>
        </div>
      </div>

      {/* Search and Filter + Vehicle Grid */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-8">
        <Card className="shadow-xl">
          <CardContent className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="relative">
                <Search className="h-5 w-5 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input
                  placeholder="Search by model, type, or stock number..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
              <div className="flex items-center space-x-2">
                <Filter className="h-5 w-5 text-slate-400" />
                <div className="flex flex-wrap gap-2">
                  {vehicleTypes.map(type => (
                    <Button
                      key={type}
                      variant={selectedType === type ? "default" : "outline"}
                      size="sm"
                      onClick={() => setSelectedType(type)}
                      className="capitalize"
                    >
                      {type}
                    </Button>
                  ))}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Vehicle Grid */}
        <div className="py-8">
          {filteredVehicles.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredVehicles.map((vehicle, index) => (
                <Card key={vehicle.id || index} className="overflow-hidden hover:shadow-lg transition-shadow">
                  {vehicle.primary_image && (
                    <div className="h-48 overflow-hidden">
                      <img
                        src={vehicle.primary_image}
                        alt={vehicle.model}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  )}
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg">{vehicle.year} {vehicle.model}</CardTitle>
                    <CardDescription>Stock: {vehicle.stock_no} • {vehicle.type}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {vehicle.description && (
                        <p className="text-sm text-slate-600 line-clamp-2">
                          {vehicle.description}
                        </p>
                      )}
                      {vehicle.features && (
                        <div className="flex flex-wrap gap-1">
                          {vehicle.features.split(',').slice(0, 3).map((feature, idx) => (
                            <Badge key={`feature-${idx}`} variant="outline" className="text-xs">
                              {feature.trim()}
                            </Badge>
                          ))}
                        </div>
                      )}
                      <div className="border-t pt-4">
                        <div className="mb-3">
                          {vehicle.public_price_usd ? (
                            <p className="text-2xl font-bold text-red-600">
                              ${vehicle.public_price_usd.toLocaleString()}
                            </p>
                          ) : (
                            <p className="text-lg font-semibold text-slate-700">
                              Contact for Price
                            </p>
                          )}
                        </div>
                        <Button
                          className="w-full bg-red-600 hover:bg-red-700"
                          onClick={() => handleInquiry(vehicle)}
                        >
                          <Mail className="h-4 w-4 mr-2" />
                          Inquire Now
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <div className="text-center py-16">
              <Car className="h-24 w-24 text-slate-300 mx-auto mb-4" />
              <h3 className="text-2xl font-semibold text-slate-700 mb-2">No Vehicles Found</h3>
              <p className="text-slate-500">
                {searchTerm || selectedType !== 'all'
                  ? 'Try adjusting your search or filters'
                  : 'Check back soon for new arrivals'}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Contact Section */}
      <div className="bg-slate-800 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-4">Interested in a Vehicle?</h2>
          <p className="text-slate-300 mb-6 max-w-2xl mx-auto">
            Contact us for more information, inspection reports, or to schedule a viewing.
          </p>
          <div className="flex flex-col md:flex-row items-center justify-center space-y-4 md:space-y-0 md:space-x-6">
            <div className="flex items-center space-x-2">
              <Mail className="h-5 w-5 text-blue-400" />
              <span>sales@globaltradecar.com</span>
            </div>
            <div className="flex items-center space-x-2">
              <Phone className="h-5 w-5 text-blue-400" />
              <span>+81 70-9270-2242</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PublicMarketplace;